import type { AxiosInstance, AxiosResponse } from "axios";
import { useClient } from "../state/client";
import { initialized } from "../utils/initializedUtil";
import type CustomerId from "../state/CustomerId";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import type { MoreAppError } from "../MoreAppError";
import ServiceAccountId from "../state/ServiceAccountId";
import type { ServiceAccount, ServiceAccountState, ServiceAccountType } from "../types/ServiceAccount";
import { minutes } from "../utils/timeUtil";
import { toServiceAccount } from "../utils/mappers/serviceAccount";
import environment from "../environment";
import { literal, nullish, object, safeParse, string } from "valibot";
import { isNil, orderBy } from "lodash-es";

let client: AxiosInstance | null = null;
let customerId: CustomerId | null = null;

export type ServiceAccountDto = {
  id: string;
  displayName: string | null;
  authType: ServiceAccountType;
  accountIdentifier: string | null;
  state: ServiceAccountState;
  meta: {
    createdOn: Date;
  };
};

type ConnectServiceAccountResponse = {
  url: string;
};
type UpdateServiceAccountRequest = {
  displayName: string;
};
type UpdateServiceAccountResponse = ServiceAccountDto;

const init = (cusId: CustomerId): void => {
  customerId = cusId;
  client = useClient();
};

const getClient = () => initialized(client, "client");

const fetchServiceAccounts = async () => {
  const { data } = await getClient().get<ServiceAccountDto[]>(`api/v1.0/customers/${customerId}/service-accounts`);

  return orderBy(data.map(toServiceAccount), ["meta.createdOn"], ["desc"]);
};

const oauth2WindowMessageSchema = object({
  messageType: literal("com.moreapp.oauth"),
  accountId: nullish(string()),
  error: nullish(string()),
});

let oauthWindow: Window | null = null;
let previousUrl: string | null = null;
const startOAuth2Flow = (url: string) =>
  new Promise<ServiceAccountId | undefined>((resolve, reject) => {
    const receiveMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) {
        // Ignore messages from other origins
        return;
      }

      const { success, output: data } = safeParse(oauth2WindowMessageSchema, event.data);
      if (!success) {
        return;
      }

      if (data.messageType === "com.moreapp.oauth") {
        if (!isNil(data.error)) {
          reject(new Error(`Failed to connect service account due to; ${data.error}`));
        } else if (!isNil(data.accountId)) {
          resolve(ServiceAccountId.from(data.accountId));
        }
      }
    };

    const openOrFocusOAuthWindow = (url: string, name: string) => {
      window.removeEventListener("message", receiveMessage);

      const strWindowFeatures = "popup=true, width=600, height=1000, top=100, left=100";

      if (isNil(oauthWindow) || oauthWindow.closed) {
        oauthWindow = window.open(url, name, strWindowFeatures);
      } else if (previousUrl !== url) {
        oauthWindow = window.open(url, name, strWindowFeatures);
        if (!isNil(oauthWindow)) {
          oauthWindow.focus();
        }
      } else {
        oauthWindow.focus();
      }

      if (!isNil(oauthWindow)) {
        // OAuth2 flow has been aborted (window close)
        const pollTimer = setInterval(() => {
          if (isNil(oauthWindow) || oauthWindow.closed) {
            clearInterval(pollTimer);
            resolve(undefined);
          }
        }, 500);
      }

      window.addEventListener("message", receiveMessage, false);
      previousUrl = url;
    };

    openOrFocusOAuthWindow(url, "MoreApp - OAuth2");
  });

const ServiceAccountService = {
  getAll: fetchServiceAccounts,
  getServiceAccounts: () =>
    useQuery<ServiceAccount[]>(() => ({
      queryKey: ["serviceAccounts"],
      queryFn: fetchServiceAccounts,
      throwOnError: true,
      staleTime: minutes(10),
    })),
  connectServiceAccount: async (type: ServiceAccountType) => {
    const redirectUrl = `${environment.origin}/oauth2/callback`;
    const { data } = await getClient().get<ConnectServiceAccountResponse>(
      `/api/v1.0/customers/${customerId}/service-accounts/url?type=${type}&redirectUrl=${redirectUrl}`,
    );
    return startOAuth2Flow(data.url);
  },
  reconnectServiceAccount: async (serviceAccountId: ServiceAccountId) => {
    const redirectUrl = `${environment.origin}/oauth2/callback`;
    const { data } = await getClient().get<ConnectServiceAccountResponse>(
      `/api/v1.0/customers/${customerId}/service-accounts/${serviceAccountId}/url?redirectUrl=${redirectUrl}`,
    );
    await startOAuth2Flow(data.url);
  },
  updateServiceAccount: (serviceAccountId: ServiceAccountId) => {
    const queryClient = useQueryClient();
    return useMutation<ServiceAccount, MoreAppError, { name: string }>(() => ({
      mutationFn: async (value) => {
        const { data } = await getClient().put<
          ServiceAccountDto,
          AxiosResponse<UpdateServiceAccountResponse>,
          UpdateServiceAccountRequest
        >(`api/v1.0/customers/${customerId}/service-accounts/${serviceAccountId}`, { displayName: value.name });
        return toServiceAccount(data);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["serviceAccounts"] });
      },
    }));
  },
  deleteServiceAccount: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, ServiceAccountId>(() => ({
      mutationFn: async (serviceAccountId) => {
        await getClient().delete(`api/v1.0/customers/${customerId}/service-accounts/${serviceAccountId}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["serviceAccounts"] });
      },
    }));
  },
  init: init,
};

export default ServiceAccountService;
